import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TeceoApiResponse } from '../../../../../lib/basicRepository';
import { getOrderSummaryQueryKey } from '../../../../../services/modules/order/order.keys';
import { IAffectedOrderProducts } from '../../../../../services/modules/order/order.types';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../context/loadingStatus/LoadingStatus.store';
import { productActionsRepository } from '../../../../products/repositories/productActions/productActions.repository';
import {
  ShopByOutcome,
  ShopByResultOutput,
} from '../../../../products/repositories/types/productActions.types';
import { isPreOrder } from '../../../../products/utils/availability.utils';
import { useOrderConditionContext } from '../../../contexts/OrderCondition.context';
import { useOrderProductsContext } from '../../../contexts/OrderProducts.context';
import { validateOrderProductsQuery } from '../../../contexts/order.utils';
import { SavedStackItem } from '../../../interfaces/use-order-item-stack.types';
import {
  getOrderCalculateUrlParams,
  invalidateAffectedOrderProductsGridQueries,
  syncSavedItemsIntoGridQueries,
} from '../../../utils/orderCalculate.utils';
import useRelatedMaterials from '../../product/grid/components/orderProductGrid/hooks/useRelatedMaterials';
import { useGridStoreApiOptional } from '../../product/grid/store/GridStoreProvider';
import { buildCombinationKey } from '../../product/grid/store/gridStore.utils';
import useProductAddedNotification from './useProductAddedNotification';

const NETWORK_EFFECTS_DEBOUNCE_MS = 400;

interface UseIncrementOrderItemParams {
  orderId: string;
}

type IncrementedResponse = Required<Pick<ShopByResultOutput, 'orderProducts' | 'grid' | 'summary'>>;

const isIncrementedResponse = (
  data: ShopByResultOutput,
): data is ShopByResultOutput & IncrementedResponse =>
  data.outcome === ShopByOutcome.INCREMENTED &&
  !!data.orderProducts &&
  !!data.grid &&
  !!data.summary;

const isAddedResponse = (
  data: ShopByResultOutput,
): data is ShopByResultOutput & Required<Pick<ShopByResultOutput, 'orderProducts'>> =>
  data.outcome === ShopByOutcome.ADDED && !!data.orderProducts?.length;

const useIncrementOrderItem = ({ orderId }: UseIncrementOrderItemParams) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const gridStoreApi = useGridStoreApiOptional();
  const { upsertOrderProducts, pushOrderProducts } = useOrderProductsContext();
  const { invalidateOrderConditions } = useOrderConditionContext();
  const { refetchProductColorsSharingMaterial } = useRelatedMaterials();
  const { notifyOutcome } = useProductAddedNotification();
  const trackRequestStatus = useLoadingStatusStore((state) => state.trackRequestStatus);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingPreOrderColorIdsRef = useRef(new Set<string>());
  const pendingAffectedOrderProductsRef = useRef<IAffectedOrderProducts[]>([]);
  const runNetworkEffectsRef = useRef<() => void>();
  const pendingShopByRef = useRef<Promise<unknown>>(Promise.resolve());

  const calculateParams = getOrderCalculateUrlParams(location.search);

  const runNetworkEffects = () => {
    const preOrderColorIds = Array.from(pendingPreOrderColorIdsRef.current);
    const affectedOrderProducts = pendingAffectedOrderProductsRef.current;

    pendingPreOrderColorIdsRef.current = new Set();
    pendingAffectedOrderProductsRef.current = [];

    if (preOrderColorIds.length) {
      refetchProductColorsSharingMaterial(orderId, preOrderColorIds);
    }

    if (affectedOrderProducts.length) {
      invalidateAffectedOrderProductsGridQueries({
        orderId,
        queryClient,
        affectedOrderProducts,
        teamId: team?.id,
        isOfflineMode: false,
      });
    }

    queryClient.refetchQueries(validateOrderProductsQuery(orderId, team?.id));
    invalidateOrderConditions();
  };

  useEffect(() => {
    runNetworkEffectsRef.current = runNetworkEffects;
  });

  useEffect(
    () => () => {
      if (debounceTimerRef.current === undefined) {
        return;
      }

      clearTimeout(debounceTimerRef.current);
      runNetworkEffectsRef.current?.();
    },
    [],
  );

  const scheduleNetworkEffects = () => {
    clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = undefined;
      runNetworkEffects();
    }, NETWORK_EFFECTS_DEBOUNCE_MS);
  };

  const toSavedItems = (grid: IncrementedResponse['grid']): SavedStackItem[] =>
    grid.map((gridItem) => ({ ...gridItem, orderId, productColorId: gridItem.color.id }));

  const reconcileGridCells = (grid: IncrementedResponse['grid']) => {
    if (!gridStoreApi) {
      return;
    }

    const itemsByCombination = new Map<string, SavedStackItem[]>();

    toSavedItems(grid).forEach((savedItem) => {
      const combinationKey = buildCombinationKey({
        orderId,
        availability: savedItem.availability,
        productColorId: savedItem.productColorId,
        deliveryWindowId: savedItem.deliveryWindowId,
      });

      const savedItems = itemsByCombination.get(combinationKey) ?? [];
      savedItems.push(savedItem);
      itemsByCombination.set(combinationKey, savedItems);
    });

    itemsByCombination.forEach((savedItems, combinationKey) => {
      gridStoreApi.getState().reconcile({ combinationKey, savedItems });
    });
  };

  const applyResponse = ({ orderProducts, grid, summary }: IncrementedResponse) => {
    upsertOrderProducts(orderProducts);
    reconcileGridCells(grid);
    syncSavedItemsIntoGridQueries({
      queryClient,
      savedItems: toSavedItems(grid),
      teamId: team?.id,
    });

    queryClient.setQueryData(
      [
        team?.id,
        ...getOrderSummaryQueryKey({ orderId, urlParams: calculateParams.urlParams?.toString() }),
      ],
      summary,
    );

    orderProducts
      .filter((orderProduct) => isPreOrder(orderProduct.availability))
      .forEach((orderProduct) =>
        pendingPreOrderColorIdsRef.current.add(orderProduct.productColorId),
      );

    pendingAffectedOrderProductsRef.current.push(...(summary.affectedOrderProducts ?? []));

    scheduleNetworkEffects();
  };

  const applyOutcome = (data: ShopByResultOutput) => {
    notifyOutcome(data);

    if (isIncrementedResponse(data)) {
      applyResponse(data);
      return;
    }

    if (isAddedResponse(data)) {
      pushOrderProducts(data.orderProducts);
    }
  };

  const enqueueShopBy = (request: () => Promise<TeceoApiResponse<ShopByResultOutput>>) => {
    const pendingShopBy = pendingShopByRef.current.then(async () => {
      const response = await request();

      if (response.ok && response.data) {
        applyOutcome(response.data);
      }

      return response;
    });

    pendingShopByRef.current = pendingShopBy.catch(() => undefined);

    return pendingShopBy;
  };

  const submitIncrement = (code: string) => {
    const pendingIncrement = enqueueShopBy(() =>
      productActionsRepository().shopByIncrement({
        orderId,
        code,
        filter: calculateParams.typedParams,
      }),
    );

    trackRequestStatus(() => pendingIncrement).catch(() => undefined);

    return pendingIncrement;
  };

  const submitAdd = (code: string) =>
    enqueueShopBy(() => productActionsRepository().shopByProduct(orderId, code));

  return { submitIncrement, submitAdd };
};

export default useIncrementOrderItem;
